import React, { useRef, useState } from 'react';
import { BuildingIcon, CheckCircle2Icon, FileSpreadsheetIcon, Loader2Icon, UploadCloudIcon, UsersIcon, XIcon } from 'lucide-react';
export type ImportKind = 'leads' | 'realtors';
export interface ImportResult {
  fileName: string;
  recordCount: number;
  duplicates: number;
}
export type ImportState = Record<ImportKind, ImportResult | null>;
export const emptyImportState: ImportState = {
  leads: null,
  realtors: null
};
interface DatabaseImportStepProps {
  imports: ImportState;
  onChange: (kind: ImportKind, result: ImportResult | null) => void;
}
const cards: {
  kind: ImportKind;
  title: string;
  blurb: string;
  columns: string;
  icon: typeof UsersIcon;
}[] = [{
  kind: 'leads',
  title: 'Leads & past clients',
  blurb: 'Borrowers you are working, plus anyone you have closed. These become your consumer conversation and past-client lists.',
  columns: 'Name, phone, email, status, loan purpose, closing date',
  icon: UsersIcon
}, {
  kind: 'realtors',
  title: 'Realtors & partners',
  blurb: 'Agents, title, and insurance contacts. We split them into existing partners and prospects so outreach is measured separately.',
  columns: 'Name, brokerage, phone, email, market, partner or prospect',
  icon: BuildingIcon
}];

/** Deterministic stand-in for a real parse of the uploaded file. */
function fakeParse(fileName: string): ImportResult {
  const seed = fileName.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  const recordCount = 60 + seed % 480;
  return {
    fileName,
    recordCount,
    duplicates: recordCount % 11
  };
}
export function DatabaseImportStep({
  imports,
  onChange
}: DatabaseImportStepProps) {
  return <div data-id="element-368" data-mp="12vfpfife8ni1">
      <h2 className="text-lg font-bold text-navy-900" data-id="element-369" data-mp="12zd8y55vvh2g">
        Bring in your existing database
      </h2>
      <p className="mt-1 text-sm text-navy-500" data-id="element-370" data-mp="3iqrfibvxx0f1">
        Upload a CSV export so your daily priorities start with real
        people. You can skip this and import later from settings.
      </p>

      <div className="mt-4 grid gap-3 lg:grid-cols-2" data-id="element-371" data-mp="1t9dsv3a45c7h">
        {cards.map(card => <ImportCard key={card.kind} {...card} result={imports[card.kind]} onResult={result => onChange(card.kind, result)} data-id="element-372" data-mp="1o8yb7zistijp" />)}
      </div>

      <p className="mt-4 rounded-xl bg-navy-50 px-4 py-3 text-xs text-navy-600" data-id="element-373" data-mp="1fds1ipgeqgbw">
        We de-duplicate against records already in the shared database and never
        overwrite another officer’s relationships. You will confirm column
        mapping before anything is created.
      </p>
    </div>;
}
function ImportCard({
  kind,
  title,
  blurb,
  columns,
  icon: Icon,
  result,
  onResult
}: {
  kind: ImportKind;
  title: string;
  blurb: string;
  columns: string;
  icon: typeof UsersIcon;
  result: ImportResult | null;
  onResult: (result: ImportResult | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Only .csv files are supported. Export your list as CSV first.');
      return;
    }
    setError(null);
    setUploading(true);
    window.setTimeout(() => {
      setUploading(false);
      onResult(fakeParse(file.name));
    }, 900);
  };
  if (result) {
    return <div className="rounded-xl border border-success-200 bg-success-50 p-4" data-id="element-374" data-mp="3uuq54quics3y">
        <div className="flex items-start gap-3" data-id="element-375" data-mp="2nrp5n0wb9ma">
          <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-success-600" aria-hidden="true" data-id="element-376" data-mp="7op6xyhzu1n9" />
          <div className="min-w-0 flex-1" data-id="element-377" data-mp="1e3q2i333whtb">
            <p className="text-sm font-semibold text-navy-900" data-id="element-378" data-mp="1j3cj8mi58g6v">{title} ready</p>
            <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-navy-600" data-id="element-379" data-mp="1j5fcrgii8rgc">
              <FileSpreadsheetIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-380" data-mp="1ho9qphkc1uo6" />
              {result.fileName}
            </p>
            <p className="mt-2 text-xs text-navy-700" data-id="element-381" data-mp="1qmlqa936g2sr">
              <span className="font-bold tabular-nums" data-id="element-382" data-mp="1qoevdjy56eyn">
                {result.recordCount}
              </span>{' '}
              records found ·{' '}
              <span className="font-bold tabular-nums" data-id="element-383" data-mp="20khxwqwwofm4">{result.duplicates}</span>{' '}
              likely duplicates to review
            </p>
          </div>
          <button type="button" onClick={() => onResult(null)} aria-label={`Remove ${title} upload`} className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-navy-500 hover:bg-white/70" data-id="element-384" data-mp="1vjuasgbssix6">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-385" data-mp="2jckgw8x0s7zj" />
          </button>
        </div>
      </div>;
  }
  return <div onDragOver={event => {
    event.preventDefault();
    setDragging(true);
  }} onDragLeave={() => setDragging(false)} onDrop={event => {
    event.preventDefault();
    setDragging(false);
    handleFile(event.dataTransfer.files[0]);
  }} className={`rounded-xl border border-dashed p-4 ${dragging ? 'border-brand-400 bg-brand-50' : 'border-navy-200 bg-white'}`} data-id="element-386" data-mp="2ee99s5y40bl0">
      <div className="flex items-start gap-3" data-id="element-387" data-mp="2f8045tq49ian">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-navy-50 text-navy-600" data-id="element-388" data-mp="2f65nirlvoccd">
          <Icon className="h-4 w-4" aria-hidden="true" data-id="element-389" data-mp="2k0jjow8d6ni3" />
        </span>
        <div className="min-w-0" data-id="element-390" data-mp="25avgtqkjbgs9">
          <p className="text-sm font-semibold text-navy-900" data-id="element-391" data-mp="257psidjqcixl">{title}</p>
          <p className="mt-0.5 text-xs text-navy-600" data-id="element-392" data-mp="2a96r22eqrtb0">{blurb}</p>
          <p className="mt-2 text-[11px] uppercase tracking-wide text-navy-400" data-id="element-393" data-mp="1w8cklpb982ez">
            Expected columns
          </p>
          <p className="text-xs text-navy-500" data-id="element-394" data-mp="216nshuhzo2uk">{columns}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-395" data-mp="6ssd6ke3w6r1">
        <input ref={inputRef} id={`import-${kind}`} type="file" accept=".csv,text/csv" className="sr-only" onChange={event => handleFile(event.target.files?.[0])} data-id="element-396" data-mp="210m88r1fz0d" />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="inline-flex h-11 items-center gap-2 rounded-lg bg-navy-800 px-4 text-sm font-semibold text-white hover:bg-navy-900 disabled:opacity-60" data-id="element-397" data-mp="3p6riqg95atvj">
          {uploading ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-398" data-mp="god8jzoo68rt" /> : <UploadCloudIcon className="h-4 w-4" aria-hidden="true" data-id="element-399" data-mp="bq1zhr6782an" />}
          {uploading ? 'Reading file…' : 'Choose file'}
        </button>
        <span className="text-xs text-navy-500" data-id="element-400" data-mp="2whc7bpdkiyz3">
          or drag a .csv file here
        </span>
      </div>

      {error ? <p role="alert" className="mt-2 text-xs font-semibold text-danger-600" data-id="element-401" data-mp="1c03166sng5hr">
          {error}
        </p> : null}
    </div>;
}